import { useCallback } from 'react'
import { Input } from '../ui/Input'
import { Select } from '../ui/Select'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { CVLabelInput } from './CVLabelInput'
import { useTranslation } from '../../contexts/LanguageContext'
import type { Language, LanguageLevel } from '../../types/cv'
import { LANGUAGE_LEVELS } from '../../types/cv'

interface LanguagesFormProps {
  data: Language[] | undefined
  onAdd: () => void
  onUpdate: (id: string, data: Partial<Language>) => void
  onRemove: (id: string) => void
  customLabel?: string
  onCustomLabelChange: (value: string) => void
}

export function LanguagesForm({
  data,
  onAdd,
  onUpdate,
  onRemove,
  customLabel,
  onCustomLabelChange,
}: LanguagesFormProps) {
  const { t } = useTranslation()

  const handleNameChange = useCallback(
    (id: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdate(id, { name: e.target.value })
    },
    [onUpdate]
  )

  const handleLevelChange = useCallback(
    (id: string) => (e: React.ChangeEvent<HTMLSelectElement>) => {
      onUpdate(id, { level: e.target.value as LanguageLevel })
    },
    [onUpdate]
  )

  return (
    <div className="space-y-4">
      <CVLabelInput value={customLabel} placeholder="Languages" onChange={onCustomLabelChange} />
      {(!data || data.length === 0) && (
        <p className="text-sm text-slate-500 italic">
          {t.languagesForm.empty}
        </p>
      )}

      {(data || []).map((lang, index) => (
        <Card key={lang.id} variant="outlined" padding="md">
          <div className="flex items-start justify-between mb-4">
            <span className="text-sm font-semibold text-slate-500">
              {t.languagesForm.itemTitle} {index + 1}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onRemove(lang.id)}
              className="text-red-500 hover:text-red-700 hover:bg-red-50"
            >
              {t.languagesForm.delete}
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label={t.languagesForm.name}
              placeholder={t.languagesForm.namePlaceholder}
              value={lang.name || ''}
              onChange={handleNameChange(lang.id)}
            />
            <Select
              label={t.languagesForm.level}
              options={LANGUAGE_LEVELS.map((l) => ({
                value: l.value || '',
                label: (l.value && t.languagesForm.levels[l.value]) || l.label,
              }))}
              value={lang.level || ''}
              onChange={handleLevelChange(lang.id)}
              placeholder={t.languagesForm.selectLevel}
            />
          </div>
        </Card>
      ))}

      <Button type="button" variant="secondary" onClick={onAdd} className="w-full">
        {t.languagesForm.add}
      </Button>
    </div>
  )
}
